import { api, ApiError, type Message, type Push, type Task } from "./api";
import { readCache, keepCached } from "./cache";
import { notify } from "./notify";
import { addEvent, applyBoot, applySync, inChat, loadRoom, removeRoom, removeTask, resendWaiting, setRead, setTyping, stopTyping, upsertMessage, upsertRoom, upsertTask, useStore } from "./store";
import { plain } from "./text";

const get = useStore.getState;
const set = useStore.setState;
const name = (id: number | null) => (id != null ? get().users[id]?.name ?? "Someone" : "Someone");
const roomTitle = (roomId: number) => {
  const r = get().rooms[roomId];
  if (!r) return "";
  if (r.kind === "group") return `#${r.name}`;
  return r.members.filter((u) => u !== get().me?.id).map(name).join(", ");
};

/** Tells the others you're typing, at most every 2 s. */
let typedAt = 0;
export function sendTyping(roomId: number, parentId: number | null) {
  if (Date.now() - typedAt < 2000 || ws?.readyState !== WebSocket.OPEN) return;
  typedAt = Date.now();
  ws.send(`typing:${roomId}:${parentId ?? 0}`);
}

/** Opens from the copy on this computer first, then catches up with the server. */
// A new deploy of the Hub: reload to get it. Straight away if you're not looking,
// otherwise as soon as you switch away (or click the pill).
let version: string | null = null;
function checkVersion(v: string) {
  if (!version) { version = v; return; }
  if (v === version || document.querySelector(".upd.web")) return;
  const reload = () => location.reload();
  if (document.visibilityState !== "visible" || !document.hasFocus()) return reload();
  const b = document.createElement("button");
  b.className = "upd web";
  b.textContent = "The Hub has been updated · Reload";
  b.onclick = reload;
  document.body.appendChild(b);
  addEventListener("blur", reload, { once: true });
}

export async function start() {
  const snap = await readCache();
  if (snap?.me) set({ ...snap, phase: "ready", pending: snap.pending.map((p) => ({ ...p, failed: p.failed ?? "Waiting for connection" })) });
  try {
    const since = snap?.serverTime;
    { const b = await api.boot(); applyBoot(b); checkVersion(b.version); }
    if (since) applySync(await api.sync(back(since)));
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) { set({ phase: "signed-out" }); return; }
    if (!snap?.me) { setTimeout(start, 3000); return; }
  }
  keepCached();
  connect();
}

const back = (iso: string) => new Date(new Date(iso).getTime() - 60_000).toISOString();

let ws: WebSocket | null = null, tries = 0, timer: number | undefined, lastSeen = Date.now();

function connect() {
  clearTimeout(timer);
  const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
  const sock = new WebSocket(url);
  ws = sock;
  sock.onmessage = (e) => {
    lastSeen = Date.now();
    if (e.data === "pong") return;
    const p = JSON.parse(e.data);
    if (p.t === "hello") return onHello();
    onPush(p as Push);
  };
  sock.onclose = (e) => {
    if (ws !== sock) return;
    set({ online: false });
    if (e.code === 4001) { set({ phase: "signed-out" }); return; }
    timer = window.setTimeout(connect, Math.min(30000, 500 * 2 ** tries++) + Math.random() * 500);
  };
}

async function onHello() {
  tries = 0;
  idleSent = null;
  reportIdle();
  set({ online: true });
  const since = get().serverTime;
  try {
    { const b = await api.boot(); applyBoot(b); checkVersion(b.version); }
    if (since) applySync(await api.sync(back(since)));
    const r = get().route;
    if (r.v === "room") loadRoom(r.id).catch(() => {});
  } catch {}
  resendWaiting();
}

// A sleeping laptop leaves a dead socket behind; check on wake and every 20 s.
setInterval(() => {
  if (ws?.readyState === WebSocket.OPEN) {
    if (Date.now() - lastSeen > 50_000) ws.close();
    else { ws.send("ping"); resendWaiting(); }
  }
}, 20_000);
const wake = () => {
  if (!ws || ws.readyState > WebSocket.OPEN) { tries = 0; connect(); }
  else { ws.send("ping"); resendWaiting(); }
};
document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && wake());
window.addEventListener("online", wake);

// Away after 10 minutes without mouse or keyboard, or while the window is hidden.
let lastInput = Date.now(), idleSent: boolean | null = null;
const isIdle = () => document.visibilityState !== "visible" || Date.now() - lastInput > 10 * 60_000;
function reportIdle() {
  const idle = isIdle();
  if (idle === idleSent || ws?.readyState !== WebSocket.OPEN) return;
  idleSent = idle;
  ws.send(idle ? "idle" : "active");
}
for (const ev of ["mousemove", "keydown", "mousedown", "wheel"]) addEventListener(ev, () => { const was = isIdle(); lastInput = Date.now(); if (was) reportIdle(); }, { passive: true });
document.addEventListener("visibilitychange", reportIdle);
setInterval(reportIdle, 30_000);

function onPush(p: Push) {
  const s = get();
  switch (p.t) {
    case "msg": {
      const known = !!s.byId[p.m.id];
      upsertMessage(p.m);
      if (!known) { stopTyping(p.m.roomId, p.m.parentId, p.m.userId); msgNotice(p.m); }
      return;
    }
    case "typing": return setTyping(p.roomId, p.parentId, p.userId);
    case "follow": return set((x) => ({ follows: { ...x.follows, [p.follow.rootId]: p.follow } }));
    case "unfollow": return set((x) => { const f = { ...x.follows }; delete f[p.rootId]; return { follows: f }; });
    case "state": return set((x) => ({ states: { ...x.states, [p.state.roomId]: p.state } }));
    case "saved": return set({ saved: Object.fromEntries(p.saved.map((x) => [x.messageId, x])) });
    case "pins": return set((x) => ({ pins: [...x.pins.filter((q) => q.roomId !== p.roomId), ...p.pins] }));
    case "scheduled": return set({ scheduled: p.scheduled });
    case "mentions-seen": return set({ mentionsSeenId: p.id });
    case "reminder": {
      const m = s.byId[p.messageId];
      api.thread(p.messageId).catch(() => null).then(() => {
        const msg = get().byId[p.messageId] ?? m;
        notify("⏰ Reminder", msg ? `${name(msg.userId)}: ${plain(msg.body)}` : "A saved message", msg ? { v: "room", id: msg.roomId, focus: msg.id, thread: msg.parentId ?? undefined } : { v: "later" }, `rem-${p.messageId}`, true);
      });
      return;
    }
    case "room": return upsertRoom(p.room);
    case "room-gone": return removeRoom(p.id);
    case "read": return setRead(p.roomId, p.lastReadId);
    case "users": return set({ users: Object.fromEntries(p.users.map((u) => [u.id, u])) });
    case "presence": return set({ presence: p.presence });
    case "task": {
      const before = s.tasks[p.task.id];
      upsertTask(p.task);
      taskNotice(before, p.task);
      return;
    }
    case "task-gone": return removeTask(p.id);
    case "tev": {
      addEvent(p.ev);
      if (p.ev.kind === "reply" && p.ev.userId !== s.me?.id) {
        const t = s.tasks[p.ev.taskId];
        notify(`${name(p.ev.userId)} · task`, `${t ? t.title + ": " : ""}${p.ev.body ?? "📎"}`, { v: "task", id: p.ev.taskId }, `tev-${p.ev.id}`);
      }
      return;
    }
  }
}

/** Who hears about a message: your setting for the chat decides; an @mention always gets through. */
function msgNotice(m: Message) {
  const s = get();
  if (m.userId === s.me?.id || m.deletedAt) return;
  const text = plain(m.body) || (m.files.length ? `📎 ${m.files[0].name}` : "");
  const mentioned = m.mentions?.includes(s.me!.id);
  const level = s.states[m.roomId]?.notify ?? "all";
  const where = roomTitle(m.roomId);
  if (mentioned) return notify(`${name(m.userId)} mentioned you · ${where}`, text, { v: "room", id: m.roomId, thread: m.parentId && !m.alsoInChat ? m.parentId : undefined, focus: m.id }, `m-${m.id}`);
  if (level !== "all") return;
  if (inChat(m)) return notify(`${name(m.userId)} · ${where}`, text, { v: "room", id: m.roomId }, `m-${m.id}`);
  // Thread-only replies reach the people following that thread.
  const follows = s.follows[m.parentId!] || s.byId[m.parentId!]?.userId === s.me?.id;
  if (follows) notify(`${name(m.userId)} replied in a thread · ${where}`, text, { v: "room", id: m.roomId, thread: m.parentId! }, `m-${m.id}`);
}

function taskNotice(before: Task | undefined, t: Task) {
  const me = get().me!;
  if (!before && t.ownerId === me.id && t.givenBy !== me.id && !t.prevTaskId) notify(`New task from ${name(t.givenBy)}`, t.title, { v: "task", id: t.id }, `t-${t.id}`);
  else if (before && before.ownerId !== me.id && t.ownerId === me.id) notify(`Task for you`, t.title, { v: "task", id: t.id }, `t-${t.id}`);
  else if (before && before.status !== "done" && t.status === "done" && t.ownerId !== me.id) notify(`${name(t.ownerId)} finished`, t.title, { v: "task", id: t.id }, `t-${t.id}-done`);
}
